import { StaffAccessNotice } from "@/components/admin/StaffAccessNotice";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { listCustomers } from "@/lib/admin-customers.functions";

const q = queryOptions({
  queryKey: ["admin", "customers"],
  queryFn: () => listCustomers(),
  staleTime: 30_000,
});

export const Route = createFileRoute("/_authenticated/admin/customers")({
  head: () => ({ meta: [{ title: "Clientes — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: CustomersPage,
  errorComponent: ({ error }) => <main className="mx-auto max-w-3xl px-4 py-10"><StaffAccessNotice error={error} /></main>,
});

function CustomersPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-4xl font-semibold">Clientes</h1>
        <p className="mt-1 text-sm text-muted-foreground">Listado agrupado por email de cliente.</p>
        <div className="mt-6"><AdminNav /></div>
        <Suspense fallback={<Skeleton className="mt-8 h-96 w-full" />}>
          <Body />
        </Suspense>
      </div>
    </div>
  );
}

function Body() {
  const { data } = useSuspenseQuery(q);
  const [search, setSearch] = useState("");
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return data.customers;
    return data.customers.filter(
      (c: any) =>
        (c.email ?? "").toLowerCase().includes(s) || (c.name ?? "").toLowerCase().includes(s),
    );
  }, [data, search]);

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Input
          className="h-10 max-w-sm"
          placeholder="Buscar por email o nombre…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-sm text-muted-foreground">{rows.length} clientes</span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
          Aún no hay clientes registrados.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Reservas</th>
                <th className="px-4 py-3">Pagadas</th>
                <th className="px-4 py-3 text-right">Gasto total</th>
                <th className="px-4 py-3">Última reserva</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c: any) => (
                <tr key={c.email} className="border-t">
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{c.email}</div>
                  </td>
                  <td className="px-4 py-3">{c.bookings}</td>
                  <td className="px-4 py-3">{c.paidBookings}</td>
                  <td className="px-4 py-3 text-right font-semibold">
                    ${Number(c.totalSpent).toLocaleString("es-MX", { maximumFractionDigits: 0 })} {c.currency}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(c.lastBookingAt).toLocaleDateString("es-MX")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
